import { createReducer, on, Action } from '@ngrx/store';
import { EntityAdapter, createEntityAdapter, EntityState } from '@ngrx/entity';

import * as FileActions from './file.actions';
import { FileEntity } from './file.models';

export const FILE_FEATURE_KEY = 'file';

export interface State extends EntityState<FileEntity> {
  selectedId?: string | number; // which File record has been selected
  loading: boolean; // if File list is loading
  uploading: boolean;
  error?: string | null; // last none error (if any)
}

export interface FilePartialState {
  readonly [FILE_FEATURE_KEY]: State;
}

export function selectFileId(file: FileEntity): string {
  // Use composite key (folder + name) to handle files with same name in different folders
  // If folder is empty or undefined, just use name for backward compatibility
  return file.folder ? `${file.folder}/${file.name}` : file.name;
}

export const fileAdapter: EntityAdapter<FileEntity> =
  createEntityAdapter<FileEntity>({
    selectId: selectFileId
  });

export const initialState: State = fileAdapter.getInitialState({
  // set initial required properties
  loading: false,
  updated: false,
  uploading: false
});

const fileReducer = createReducer(
  initialState,

  on(FileActions.loadFiles, state =>
    fileAdapter.removeAll({
      ...state,
      loading: true,
      error: null
    })
  ),
  on(FileActions.loadFilesSuccess, (state, { files }) =>
    fileAdapter.setAll(files, { ...state, loading: false })
  ),
  on(FileActions.loadFilesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(FileActions.getFile, state => ({ ...state, updated: false, error: null })),
  on(FileActions.getFileSuccess, (state, { file }) => {
    // Use composite key (folder/name) to match the selectFileId function
    const fileId = selectFileId(file);

    if (state.entities[fileId]) {
      // Preserve existing folder and tags if file doesn't have them (from getFile response)
      const existingFile = state.entities[fileId];
      const fileWithFolder = {
        ...file,
        folder: file.folder || existingFile.folder,
        tags: file.tags && file.tags.length ? file.tags : (existingFile.tags || []),
        loaded: true
      };
      return fileAdapter.updateOne(
        { id: fileId, changes: fileWithFolder },
        { ...state, updated: true }
      );
    }

    return fileAdapter.addOne(
      {
        ...file,
        loaded: true
      },
      { ...state }
    );
  }),
  on(FileActions.getFileFailure, (state, { error, fileName, folder }) => {
    const fileId = folder ? `${folder}/${fileName}` : fileName;
    if (state.entities[fileId]) {
      return fileAdapter.updateOne(
        { id: fileId, changes: { loaded: true } },
        { ...state, error }
      );
    }
    return { ...state, error };
  }),

  on(FileActions.uploadFile, state => ({
    ...state,
    uploading: true,
    error: null
  })),
  on(FileActions.uploadFileSuccess, (state, { file }) =>
    fileAdapter.addOne(file, { ...state, uploading: false })
  ),
  on(FileActions.uploadFileFailure, (state, { error }) => ({
    ...state,
    uploading: false,
    error
  })),

  on(FileActions.deleteFile, state => ({ ...state, error: null })),
  on(FileActions.deleteFileSuccess, (state, { fileName, folder }) => {
    const trimmedFolder =
      folder != null && typeof folder === 'string' ? folder.trim() : '';
    const fileId = selectFileId({
      name: fileName,
      folder: trimmedFolder
    } as FileEntity);
    return fileAdapter.removeOne(fileId, state);
  }),
  on(FileActions.deleteFileFailure, (state, { error }) => ({ ...state, error }))
);

export function reducer(state: State | undefined, action: Action) {
  return fileReducer(state, action);
}
